import React, { Fragment, memo, useState, useEffect, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import Filters, { FiltersProvider } from 'components/Filters';
import QuickFilters from 'components/QuickFilters';
import { intercomHideAndShow } from 'utils/custom';

import styles from './styles';
const useStyles = createUseStyles(styles, 'SectionFilter');
function SectionFilter({
    useQuickFilters,
    quickFilters,
    isOpen,
    onClose,
    onChange,
    quickSchema,
    schema,
    id,
    entityLogName,
    logEvent,
    className,
    initialValues = {},
    values: valuesProp,
    toggleQuickFilter,
    onReOrder,
    isPinnable = false,
    noFiltersProvider = false,
    defaultOpenFilters = {},
}) {
    const classes = useStyles();
    const { t } = useWeb5Context();
    const [openFilters, setOpenFilters] = useState(defaultOpenFilters);
    const rootClassName = classnames(classes.root, className, {
        [classes.filtersActive]: isOpen,
    });
    const [values, setValues] = useState(initialValues);

    useEffect(() => {
        setValues(valuesProp);
    }, [valuesProp]);

    useEffect(() => {
        if (
            !schema ||
            !openFilters.length ||
            (openFilters && Object.keys(openFilters).length) ||
            schema.length > 4
        )
            return;
        setOpenFilters(schema.reduce((obj, el) => ({ ...obj, [el.id]: true }), {}));
    }, [openFilters, schema]);

    const finalSchema = useMemo(() => {
        schema?.map((el) => {
            el.fields.map((field) => {
                if (field?.inputProps) {
                    field.inputProps.className = `section-filter__field__${field.id}`;
                    field.inputProps.classes = {
                        input: `section-filter__field__${field.id}--input`,
                        clear: `section-filter__field__${field.id}--clear`,
                        calendarIcon: `section-filter__field__${field.id}--calendar`,
                        clockIcon: `section-filter__field__${field.id}--clock`,
                    };
                }
                return field;
            });
            // locator if filter is open or not
            if (openFilters[el.id]) {
                el.className = `section-filter__${el.id}--opened`;
            } else {
                el.className = `section-filter__${el.id}`;
            }

            return el;
        });
        return schema;
    }, [schema, openFilters]);

    const onChangeFilter = useCallback(
        ({ filter, value }) => {
            const newValues = { ...values, [filter.id]: value };
            if (value === null || value === undefined || value === '') delete newValues[filter.id];
            !valuesProp && setValues(newValues);
            onChange && onChange(newValues);
            if (entityLogName && logEvent)
                logEvent({
                    event: entityLogName,
                    functionality: 'filter',
                });
        },
        [values, valuesProp, onChange, entityLogName, logEvent],
    );

    const onToggleFilter = useCallback(
        ({ filter, hasSubfilters = false, parentFilter = null, overrideOpen = false }) => {
            let newOpenFilters = {};

            // Helper function to set the subfilters values
            const toggleSubfilters = (subfilters = {}, filter = null) => {
                const newSubfilters = Object.entries(subfilters).reduce((obj, [key, value]) => {
                    obj[key] = key === filter ? !value : false; // Toggle the specified subfilter and close others
                    return obj;
                }, {});
                if (filter && !newSubfilters.hasOwnProperty(filter)) {
                    newSubfilters[filter] = true;
                }
                return newSubfilters;
            };

            if (!parentFilter) {
                // Toggle main level filters
                newOpenFilters = Object.entries(openFilters).reduce((obj, [key, value]) => {
                    let newValue;

                    switch (true) {
                        case key === filter && hasSubfilters:
                            newValue = { ...value, open: overrideOpen || !value.open };
                            break;
                        case key === filter && !hasSubfilters:
                            newValue = overrideOpen || !value;
                            break;
                        default:
                            newValue =
                                typeof value === 'boolean'
                                    ? overrideOpen || false
                                    : { ...value, open: overrideOpen || false };
                            break;
                    }

                    obj[key] = newValue;

                    return obj;
                }, {});
                if (!newOpenFilters.hasOwnProperty(filter)) {
                    newOpenFilters[filter] = hasSubfilters ? { open: true, subfilters: {} } : true;
                }
            } else {
                // Toggle subfilters
                newOpenFilters = Object.entries(openFilters).reduce((obj, [key, value]) => {
                    if (key === parentFilter) {
                        obj[key] = {
                            open: true,
                            subfilters: toggleSubfilters(value.subfilters, filter),
                        };
                    } else {
                        obj[key] = typeof value === 'boolean' ? false : { ...value, open: false };
                    }
                    return obj;
                }, {});
                if (!newOpenFilters.hasOwnProperty(parentFilter)) {
                    newOpenFilters[parentFilter] = {
                        open: true,
                        subfilters: toggleSubfilters({}, filter),
                    };
                }
            }

            setOpenFilters(newOpenFilters);
        },
        [openFilters],
    );

    const onClearFilters = useCallback(() => {
        !valuesProp && setValues(initialValues);
        onChange(initialValues);
    }, [initialValues, onChange, valuesProp]);

    const labels = useMemo(
        () => ({ titleFilters: t('cfm_label_filters'), clearAll: t('cfm_action_clear_filters') }),
        [t],
    );

    useEffect(() => {
        if (isOpen) {
            intercomHideAndShow('hide');
        } else {
            intercomHideAndShow('show');
        }
    }, [isOpen]);

    const FiltersProviderElement = noFiltersProvider ? Fragment : FiltersProvider;

    if (useQuickFilters)
        return (
            <FiltersProviderElement>
                <QuickFilters
                    filters={quickFilters}
                    schema={schema}
                    toggled={openFilters}
                    onToggle={onToggleFilter}
                    onChange={onChangeFilter}
                    onClearAll={onClearFilters}
                    values={values}
                    className={classes.quickFilters}
                />
            </FiltersProviderElement>
        );

    return (
        <main id={id} className={rootClassName}>
            <FiltersProviderElement>
                <Filters
                    isOpen={isOpen}
                    toggled={openFilters}
                    schema={finalSchema}
                    labels={labels}
                    showAdd={false}
                    onToggle={onToggleFilter}
                    onChange={onChangeFilter}
                    onClearAll={onClearFilters}
                    onClose={onClose}
                    values={values}
                    useViewsInFilters={false}
                    isPinnable={isPinnable}
                    toggleQuickFilter={toggleQuickFilter}
                    quickSchema={quickSchema}
                    onReOrder={onReOrder}
                />
            </FiltersProviderElement>
        </main>
    );
}

SectionFilter.propTypes = {};

export default memo(SectionFilter);
